import React, { useContext, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import { renderCount } from "../../../../../appHelpers/appLoggers";
import { AlertsContext } from "./_context/AlertsContext";
import { AlertsFC } from "./AlertsFC";
import { CompactAlertSummary } from "./_helpers/CompactAlertSummary";
import { MachinesContext } from "./_context/MachinesContext";
import { MachinesTableFC } from "./MachinesTableFC";
import { LoaderFC } from "../../_helpers/LoaderFC";
import { HHMTableFC } from "./HHMTableFC";
import { InputContext } from "../../_context/InputContext";
import { UserContext } from "../../_context/UserContext";
import { useHistory } from "react-router-dom";
import { getMessage } from "../../_utils/util_general";
import { ErrorFC } from "../../_misc/ErrorFC";
import { TableEDU } from "./TableEDU";
import { Avatar } from "../../../../../appHelpers/appCommonSC";
import { SegmentedPicker, GlassCard } from "../../../../ui";

export const _DashboardFC = () => {
  // renderCount(`_DashboardFC`);
  const history = useHistory();
  const { getUserProfile, user } = useContext(UserContext);
  const [activeKey, setActiveKey] = useState("1");

  const {
    loadingAlertConfigs,
    loadingDashboardAlerts,
    alertConfigs,
    defaultAlertConfigs,
    dashboardAlerts,
    getDashboardAlerts,
    getAlertConfigs,
  } = useContext(AlertsContext);

  const {
    loadingMachines,
    loadingHHMMachines,
    loadingNetworkIssues,
    setLoadingMachines,
    setLoadingHHMMachines,
    machines,
    networkIssues,
    HHMMachines,
    getMachines,
    getNetworkIssues,
    getHHMMachines,
    getMachinesError,
    getHHMMachinesError,
    displayRecentValues,
    getRecentValues,
  } = useContext(MachinesContext);

  const { viewToggle, setViewToggle } = useContext(InputContext);

  useEffect(() => {
    // renderCount(`_DashboardFC - useEffect`);
    // getAlerts();
    getMachines();
    getNetworkIssues();
    getHHMMachines();
    getAlertConfigs();
    getDashboardAlerts();
    getUserProfile();
    getRecentValues();

    return () => {
      // RESET UPON UN-MOUNTING
      // INSTEAD OF UNMOUNTING WE'LL MANUALLY REFRESH
      // setLoadingAlertConfigs(true);
      // setLoadingMachines(true);
      // setLoadingHHMMachines(true);
    };
  }, []);

  // MEMO ALLOWS FOR SPECIFIC STATE DEPENDENCY CONTROL OF CHILD RENDERS
  const memoMachines = useMemo(() => {
    return (
      <MachinesTableFC
        machines={machines}
        defaultAlertConfigs={defaultAlertConfigs}
        alertConfigs={alertConfigs}
        networkIssues={networkIssues}
        dashboardAlerts={dashboardAlerts}
      />
    );
  }, [machines, alertConfigs, dashboardAlerts]);

  const memoEDUs = useMemo(() => {
    return (
      <TableEDU
        machines={machines}
        defaultAlertConfigs={defaultAlertConfigs}
        alertConfigs={alertConfigs}
        networkIssues={networkIssues}
        dashboardAlerts={dashboardAlerts}
      />
    );
  }, [machines, alertConfigs, dashboardAlerts]);

  const memoHHMMachines = useMemo(() => {
    return (
      <HHMTableFC
        machines={HHMMachines}
        defaultAlertConfigs={defaultAlertConfigs}
        alertConfigs={alertConfigs}
        networkIssues={networkIssues}
        dashboardAlerts={dashboardAlerts}
      />
    );
  }, [HHMMachines, alertConfigs, dashboardAlerts]);

  const getNavigationOptions = () => {
    const options = [];

    if (machines.length > 0) {
      options.push({ eventKey: "MMB", label: "Mag Data" });
      options.push({ eventKey: "EDU", label: "Env Data" });
    }

    if (HHMMachines.length > 0) {
      options.push({ eventKey: "HHM", label: "Log Data" });
    }

    return options;
  };

  useEffect(() => {
    if (!loadingHHMMachines && !loadingMachines) {
      if (machines?.length > 0 && !viewToggle) {
        setViewToggle("MMB");
      } else if (HHMMachines?.length > 0 && !viewToggle) {
        setViewToggle("HHM");
      }
    }
  }, [
    loadingHHMMachines,
    loadingMachines,
    machines?.length,
    HHMMachines?.length,
    viewToggle,
  ]);

  return (
    <DashboardSC>
      {loadingMachines ||
      loadingHHMMachines ||
      loadingAlertConfigs ||
      loadingNetworkIssues ? (
        <LoaderFC />
      ) : (
        <>
          {/* Header with greeting and avatar */}
          {/* Navigation */}
          <NavigationContainer>
            <SegmentedPicker
              options={getNavigationOptions()}
              activeKey={viewToggle}
              onSelect={setViewToggle}
              variant="glass"
              size="medium"
            />
          </NavigationContainer>

          {/* Compact Alert Summary */}
          {!loadingDashboardAlerts && dashboardAlerts.length > 0 ? (
            <CompactAlertSummary
              alerts={dashboardAlerts}
              systems={machines}
              hhmSystems={HHMMachines}
              currentView={viewToggle}
              recentValues={displayRecentValues}
            />
          ) : null}

          {/* Content */}
          <ContentContainer>
            {viewToggle === "MMB" && !getMachinesError ? (
              memoMachines
            ) : viewToggle === "HHM" && !getHHMMachinesError ? (
              memoHHMMachines
            ) : viewToggle === "EDU" && !getMachinesError ? (
              memoEDUs
            ) : getMachinesError || getHHMMachinesError ? (
              <ErrorFC statusCode="500" />
            ) : null}
          </ContentContainer>
        </>
      )}
    </DashboardSC>
  );
};

const DashboardSC = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  width: 100%;
  opacity: ${({ loadingDashboard }) => (loadingDashboard ? 0 : 1)};
  transition: opacity 0.25s;
  padding: 20px;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 32px;

  @media screen and (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
